let mapsLoading: Promise<void> | undefined;
type MapsCallbackWindow = Window & { __apexosGoogleMapsReady?: () => void };

/** Load once; Google invokes the callback after the requested libraries are ready. */
export function loadGoogleMapsScript(): Promise<void> {
  if (mapsLoading) return mapsLoading;
  if (window.google?.maps) return Promise.resolve();

  mapsLoading = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps')), { once: true });
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      reject(new Error('Google Maps API key is not configured'));
      return;
    }

    const script = document.createElement('script');
    const callbackWindow = window as MapsCallbackWindow;
    callbackWindow.__apexosGoogleMapsReady = () => {
      delete callbackWindow.__apexosGoogleMapsReady;
      resolve();
    };
    // With loading=async the DOM load event is not the API-ready signal.
    // DrawingManager has been removed; drawing tools are implemented locally.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places,geometry&loading=async&callback=__apexosGoogleMapsReady`;
    script.async = true;
    script.onerror = () => {
      delete callbackWindow.__apexosGoogleMapsReady;
      script.remove();
      reject(new Error('Failed to load Google Maps API'));
    };
    document.head.appendChild(script);
  }).catch(error => {
    mapsLoading = undefined;
    throw error;
  });
  return mapsLoading;
}
/**
 * Resolve a one-line address to coordinates.
 *
 * Used when seeding a form from a record that stores an address but no
 * geocode - a Job, for instance - so the user is not made to re-run an address
 * search for a property the system already knows. Resolves to null on any
 * failure; callers treat a missed geocode as "fall back to manual entry".
 */
export async function geocodeAddressLine(
  address: string,
): Promise<{ lat: number; lng: number } | null> {
  if (!address.trim()) return null;

  try {
    await loadGoogleMapsScript();
  } catch {
    return null;
  }

  const maps = window.google?.maps;
  if (!maps?.Geocoder) return null;

  return new Promise((resolve) => {
    new maps.Geocoder().geocode({ address }, (results: any, status: any) => {
      if (status !== 'OK' || !results?.length) {
        resolve(null);
        return;
      }
      const location = results[0].geometry?.location;
      if (!location) {
        resolve(null);
        return;
      }
      resolve({ lat: location.lat(), lng: location.lng() });
    });
  });
}
